import moment, { type Moment } from "moment";

import { updateQueryParam } from "@/lib/url.js";

// The schedule board's date is URL-driven: a bare URL means today, so a
// navigation to today's date clears the param instead of writing it.
// A Moment argument keeps its UTC/zone mode, so callers own the timezone
// semantics of the date they pass (FullCalendar emits UTC-fielded dates).
export function syncStartParam(
  value: Date | string | Moment,
  { replace = false } = {}
): void {
  const day = moment(value).format("YYYY-MM-DD");
  const isToday = day === moment().format("YYYY-MM-DD");
  updateQueryParam("start", isToday ? null : day, replace);
}

// Pass `query` from a reactive route when the value must track client-side
// navigation; omit it to read the live URL. A bare URL means today, so null
// is a real answer here rather than a missing one.
export function currentStartParam(
  query?: Record<string, unknown>
): string | null {
  const raw = query
    ? query.start
    : new URLSearchParams(window.location.search).get("start");
  return typeof raw === "string" && raw ? raw : null;
}

// Carries the board date onto a link that leaves the board, so returning to
// the board can restore the day. Idempotent, and preserves any query the
// path already has.
export function withStartParam(
  path: string,
  query?: Record<string, unknown>
): string {
  const start = currentStartParam(query);
  if (!start) return path;

  const [base, search = ""] = path.split("?");
  const params = new URLSearchParams(search);
  params.set("start", start);
  return `${base}?${params}`;
}

// Carries the board date onto a board-bound href, owning the rule for every
// caller: the anchor interceptor at click time, and the sidebar Link at render
// time (pass `query` there — it persists across client-side navigation and
// cannot observe window.location). Unlike withStartParam it is add-if-absent,
// never overwrite: a board link that already names a day did so deliberately
// (the job page's "View on Schedule" targets the appointment's own day).
//
// Runs on every intercepted anchor click, so it must never throw — an
// unparsable href comes back unchanged for the browser to deal with. Output
// mirrors the input's shape: absolute in, absolute out (the interceptor's Vue
// branch parses the result with no base); relative in, relative out (a
// RouterLink `to` must stay relative).
export function carryStartOntoBoardHref(
  href: string,
  query?: Record<string, unknown>
): string {
  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) return href;
    if (url.pathname !== "/") return href;
    if (url.searchParams.has("start")) return href;

    const start = currentStartParam(query);
    if (!start) return href;

    url.searchParams.set("start", start);
    return /^https?:\/\//i.test(href)
      ? url.toString()
      : url.pathname + url.search + url.hash;
  } catch {
    return href;
  }
}
